// Egy számlázási profil megjelenítése a lista „Számlázási adatok" oszlopához (legacy billing_profiles-parity).
// Sorok (a legacy sorrendben): billingName (erős) · cím egy sorban (halvány) · countryCode-chip + adószám ·
// „EU"-chip + EU-adószám. A címet a zip/city/address-ből tisztán rakjuk össze (nincs felesleges vessző).
// Az ExpandableCell renderItem-je hívja (statikus és legördülő nézetben egyaránt).
import { type ClientBillingProfileSchema } from '@atlas/contracts';
import { type z } from 'zod';

import { Text } from '@/components/ui/text';

type BillingProfile = z.infer<typeof ClientBillingProfileSchema>;

/** „{zip} {city}, {address}" — üres részeket tisztán kihagyva (nincs lezáró vessző/dupla szóköz). */
function formatAddress(p: BillingProfile): string {
  const locality = [p.zipCode, p.city].filter(Boolean).join(' ').trim();
  return [locality, p.address].filter((s) => s?.trim()).join(', ');
}

/**
 * A profil SZÖVEGES alakja az EXPORTHOZ — ugyanaz a négy adat, ugyanabban a sorrendben, mint amit a cella
 * rajzol (név · cím · adószám országkóddal · EU-adószám), csak chip-ek helyett sortöréssel.
 *
 * MIÉRT ITT ÉS NEM A LISTA-OLDALON: így a megjelenítés és az export EGY fájlban dől el. Ha a cella egyszer
 * új adatot mutat, ez a függvény ott van mellette — a lista-oldalon lévő párhuzamos leképezés csendben
 * elmaradna tőle (és az exportból hiányozna, amit a felhasználó a képernyőn lát).
 */
export function billingProfileExportText(p: BillingProfile): string {
  return [
    p.billingName,
    formatAddress(p),
    p.taxNumber ? `${p.countryCode} ${p.taxNumber}` : '',
    p.euTaxNumber ? `EU ${p.euTaxNumber}` : '',
  ]
    .filter((x) => x.trim() !== '')
    .join('\n');
}

function TaxChip({ label }: { label: string }): JSX.Element {
  return (
    <span className="bg-muted text-muted-foreground rounded px-1 py-px text-xs font-medium leading-none">
      {label}
    </span>
  );
}

export function BillingProfileCell({ profile }: { profile: BillingProfile }): JSX.Element {
  const address = formatAddress(profile);
  // Profil-név: a cella alap-stílusa (13/muted). A cím + adószám EGGYEL halványabb: `tone="tertiary"`
  // (13/tertiary/light) — pontosan mint az accounts Cím-oszlopában az ország-sor.
  return (
    <div className="min-w-0 space-y-0.5">
      <div className="truncate">{profile.billingName}</div>
      {address && (
        <Text tone="tertiary" as="div" className="truncate">
          {address}
        </Text>
      )}
      {profile.taxNumber && (
        <div className="flex items-center gap-1">
          <TaxChip label={profile.countryCode} />
          <Text tone="tertiary" as="span" className="truncate">
            {profile.taxNumber}
          </Text>
        </div>
      )}
      {profile.euTaxNumber && (
        <div className="flex items-center gap-1">
          <TaxChip label="EU" />
          <Text tone="tertiary" as="span" className="truncate">
            {profile.euTaxNumber}
          </Text>
        </div>
      )}
    </div>
  );
}
